import { useTranslation } from 'react-i18next';
import type { FormSummary } from '@/api';
import { UiLink } from '@/components/Ui/UiLink';
import { listTime, parseTimestamp } from '@/lib/relative-time';
import classes from './cells.module.css';

export function lastMessageTime(f: FormSummary): number {
  return f.last_message_at ? parseTimestamp(f.last_message_at).getTime() || 0 : 0;
}

export function byLatestMessage(a: FormSummary, b: FormSummary): number {
  return lastMessageTime(b) - lastMessageTime(a) || a.title.localeCompare(b.title);
}

export function FormNameCell({ form }: { form: FormSummary }) {
  return (
    <UiLink to={`/account/forms/${form.id}`}>
      <strong>{form.title}</strong>
    </UiLink>
  );
}

export function LastMessageCell({ form }: { form: FormSummary }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.split('-')[0];
  return (
    <span className={classes.nowrap}>
      {form.last_message_at ? listTime(form.last_message_at, locale) : t('forms.list.never')}
    </span>
  );
}

export function RecipientsCell({ form }: { form: FormSummary }) {
  const { t } = useTranslation();
  if (form.recipients_verified === 0) {
    return (
      <span className={classes.risk}>
        <span className={classes.dot} aria-hidden="true" />
        {form.recipients_pending > 0
          ? t('forms.list.recipientsNonePending', { count: form.recipients_pending })
          : t('forms.list.recipientsNone')}
      </span>
    );
  }
  return (
    <span>
      {form.recipients_pending > 0
        ? t('forms.list.recipientsWithPending', {
            verified: form.recipients_verified,
            count: form.recipients_pending,
          })
        : form.recipients_verified}
    </span>
  );
}

export function ProblemsCell({ form }: { form: FormSummary }) {
  const { t } = useTranslation();
  if (form.problems_action > 0) {
    return (
      <UiLink to={`/account/forms/${form.id}`} className={classes.risk}>
        <span className={classes.dot} aria-hidden="true" />
        {t('forms.list.problemCount', { count: form.problems_action })}
      </UiLink>
    );
  }
  return (
    <span className={classes.sub}>
      {form.problems_waiting > 0 ? t('forms.list.waiting') : t('forms.list.noProblems')}
    </span>
  );
}

export function protectionText(form: FormSummary, t: (key: string) => string): string {
  const parts = [
    form.check_challenge ? t('forms.list.captcha') : null,
    form.filter_spam ? t('forms.list.spamFilter') : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : t('forms.list.noProtection');
}
